import { Link } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { CONTEXTUAL_HELP } from "@/data/helpContent";

/**
 * ContextualHelp - A small help link that opens a specific help article
 * 
 * Usage:
 * <ContextualHelp helpKey="availability" />
 * <ContextualHelp articleId="set-availability" text="Learn how to set availability" />
 */
export default function ContextualHelp({ 
  helpKey,  // Use predefined key from CONTEXTUAL_HELP
  articleId, // Or specify articleId directly
  text,      // Custom text (overrides helpKey text)
  className = "",
  iconOnly = false,
  size = "sm" // "sm" | "xs"
}) {
  const helpData = helpKey ? CONTEXTUAL_HELP[helpKey] : null;
  const finalArticleId = articleId || helpData?.articleId;
  const displayText = text || helpData?.text || "Need help?";

  if (!finalArticleId) return null;

  const sizeClasses = size === "xs" 
    ? "text-[10px] gap-1" 
    : "text-xs gap-1.5";

  if (iconOnly) {
    return (
      <Link
        to={`/app/support?article=${finalArticleId}`}
        className={`text-[#757675] hover:text-[#205B5A] transition-colors ${className}`}
        title={displayText}
      >
        <HelpCircle className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      </Link>
    );
  }

  return (
    <Link
      to={`/app/support?article=${finalArticleId}`}
      className={`inline-flex items-center ${sizeClasses} text-[#757675] hover:text-[#205B5A] transition-colors ${className}`}
      data-testid={`help-link-${helpKey || finalArticleId}`}
    >
      <HelpCircle className={size === "xs" ? "w-3 h-3" : "w-3.5 h-3.5"} />
      <span>{displayText}</span>
    </Link>
  );
}

/**
 * HelpTooltip - A help icon that shows a tooltip on hover
 */
export function HelpTooltip({ text, articleId }) {
  return (
    <div className="relative group inline-block">
      <Link
        to={articleId ? `/app/support?article=${articleId}` : "/app/support"}
        className="text-[#757675] hover:text-[#205B5A] transition-colors"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </Link>
      {text && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-[#1A1B1A] text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1A1B1A]" />
        </div>
      )}
    </div>
  );
}
